import { cn } from '@/helpers/classnames';
import { useTenantID } from '@/hooks/useTenantID';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import { IconCheckCircle, IconXMarkCircle } from '@/icons';
import { useMemo } from 'react';

export interface PayerListItemData {
  id: string;
  orgName: string;
  orgId: string;
  status: 'incomplete' | 'complete';
  isInternal: boolean;
  detailsString: string;
  createdAt: string;
  updatedAt: string;
}

export const PayerListItem = ({
  id,
  orgName,
  orgId,
  status,
  isInternal,
  detailsString,
}: PayerListItemData) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();

  const { StatusIcon, iconColor, statusLabel } = useMemo(() => {
    const isIncomplete = status === 'incomplete';
    return {
      StatusIcon: isIncomplete ? IconXMarkCircle : IconCheckCircle,
      iconColor: isIncomplete ? 'text-red-500' : 'text-green-500',
      statusLabel: isIncomplete
        ? formatMessage({ defaultMessage: 'Måste kompletteras' })
        : formatMessage({ defaultMessage: 'Godkänd' }),
    };
  }, [status, formatMessage]);

  return (
    <Button
      id={id}
      to="/$tenantId/organizations/organization/$organizationId/$view"
      params={{
        tenantId,
        organizationId: orgId,
        view: 'payers',
      }}
      className="h-fit w-full justify-between gap-x-5 p-3 font-normal"
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-x-5">
        <div className="flex flex-col items-start">
          <p>{orgName}</p>
          <p className="text-text-800">
            {isInternal ? (
              <FormattedMessage defaultMessage="Intern" />
            ) : (
              <FormattedMessage defaultMessage="Extern" />
            )}
          </p>
        </div>
        <p className="text-left font-light text-text-800">{detailsString}</p>
      </div>
      <div
        className="ml-auto flex items-center"
        role="status"
        aria-label={statusLabel}
      >
        <StatusIcon
          className={cn('size-5 min-w-5', iconColor)}
          aria-hidden="true"
        />
      </div>
    </Button>
  );
};
